// 검색형 콤보박스 — native <select>가 검색(조회) 불가한 문제를 해결한다.
// 한글 label 우선 표시(키는 회색 모노 보조), group 카테고리 헤더, kind 배지, 타이핑 필터.
// 외부 라이브러리 없이 input + 드롭다운을 직접 구현(기존 admin/Tailwind 패턴).
// 결정 기록: docs/decisions/2026-06-22-composer-binding-visibility-korean-ux.md

import { useEffect, useMemo, useRef, useState } from 'react';

/** 콤보박스 항목. group이 같은 항목들은 카테고리 헤더로 묶인다. */
export interface ComboboxOption {
  /** 선택 시 onChange로 전달되는 값 */
  value: string;
  /** 한글 라벨(주 표시) */
  label: string;
  /** 개발자용 보조 키(회색 모노) */
  key?: string;
  /** 카테고리 헤더로 묶을 그룹명(한글) */
  group?: string;
  /** 우측 배지(kind/타입 등) */
  badge?: string;
}

interface Props {
  value: string | undefined;
  options: ComboboxOption[];
  onChange: (value: string | undefined) => void;
  /** 미선택 상태 placeholder */
  placeholder?: string;
  /** (미선택) 항목을 목록 상단에 노출해 값 비우기 허용 */
  allowClear?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  /**
   * 드롭다운 옵션 호버 시 그 option.value(미호버/드롭다운 닫힘/언마운트 시 undefined)를 통지.
   * R2 — 호버한 "기존 값" 바인딩의 캔버스 셀/노드를 하이라이트하는 데 쓴다.
   */
  onHoverOption?: (value: string | undefined) => void;
  /**
   * 닫힘 → 열림 전환 조작 통지 — lazy 옵션 조회의 트리거로 쓴다(분할 그룹 캐스케이드 칸,
   * 결정 2026-09-04 개정 절). 부수효과 있는 조회를 "여는 조작"에 구조적으로 묶기 위한 것이라
   * 열릴 때만 부르고, 조회 중복 방지(캐시)는 호출부 몫이다.
   */
  onOpen?: () => void;
  /** 필터 결과가 없을 때 문구(기본 '결과 없음') — 로딩 중/빈 조회를 호출부가 구분해 말할 수 있게. */
  emptyText?: string;
}

const FIELD_CLS =
  'w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400';

/** label·key·group 부분일치(대소문자 무시) 필터. */
function matches(opt: ComboboxOption, q: string): boolean {
  if (!q) return true;
  const hay = `${opt.label} ${opt.key ?? ''} ${opt.group ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

export function CatalogCombobox({
  value,
  options,
  onChange,
  placeholder = '검색...',
  allowClear = false,
  clearLabel = '(미선택)',
  disabled = false,
  onHoverOption,
  onOpen,
  emptyText = '결과 없음',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // onHoverOption은 호출부에서 인라인 화살표로 넘어와 매 렌더 identity가 바뀐다. effect deps에 직접
  // 넣으면 호버 → 상위 setState → 재렌더 → effect 재실행(클리어) 루프가 생기므로 ref로 최신값만 참조.
  const hoverRef = useRef(onHoverOption);
  hoverRef.current = onHoverOption;

  // 드롭다운이 닫히면(선택/외부클릭/Esc 등 모든 경로) 호버 하이라이트를 반드시 클리어.
  useEffect(() => {
    if (open) return;
    hoverRef.current?.(undefined);
  }, [open]);
  // 언마운트(입력 행 제거/패널 전환) 시에도 클리어.
  useEffect(() => () => hoverRef.current?.(undefined), []);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  // 외부 클릭 → 닫기.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(
    () => options.filter((o) => matches(o, query)),
    [options, query],
  );

  // group → 항목 묶기(선언 순서 유지).
  const groups = useMemo(() => {
    const map = new Map<string, ComboboxOption[]>();
    for (const o of filtered) {
      const g = o.group ?? '';
      const arr = map.get(g);
      if (arr) arr.push(o);
      else map.set(g, [o]);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const choose = (v: string | undefined) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  return (
    // flex-1/min-w-0: flex 부모(인라인 라벨 레이아웃)에선 남은 폭을 채우고, block 부모에선 무해.
    <div ref={rootRef} className="relative flex-1 min-w-0">
      {/* 닫힘: 선택값 표시 버튼. 열림: 검색 input. */}
      {open ? (
        <input
          type="text"
          autoFocus
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
          }}
          className={FIELD_CLS}
          role="combobox"
          aria-expanded={true}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setOpen(true);
            onOpen?.();
          }}
          className={`${FIELD_CLS} text-left flex items-center justify-between gap-1 ${
            disabled ? 'bg-gray-100 text-gray-400' : ''
          }`}
          aria-haspopup="listbox"
          aria-expanded={false}
        >
          {selected ? (
            <span className="truncate min-w-0">
              <span>{selected.label}</span>
              {selected.key && (
                <span className="ml-1 text-[10px] text-gray-400 font-mono">{selected.key}</span>
              )}
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          <span className="text-gray-400 text-[10px]">▾</span>
        </button>
      )}

      {open && (
        <div
          role="listbox"
          onMouseLeave={() => onHoverOption?.(undefined)}
          className="absolute z-30 mt-0.5 w-full max-h-60 overflow-y-auto overflow-x-hidden rounded border border-gray-300 bg-white shadow-lg"
        >
          {allowClear && (
            <button
              type="button"
              onClick={() => choose(undefined)}
              className="w-full text-left text-xs px-2 py-1 text-gray-500 hover:bg-brand-50"
            >
              {clearLabel}
            </button>
          )}
          {groups.length === 0 ? (
            <p className="text-xs text-gray-400 px-2 py-2">{emptyText}</p>
          ) : (
            groups.map(([group, opts]) => (
              <div key={group}>
                {group && (
                  <p className="text-[10px] text-gray-400 font-semibold bg-gray-50 px-2 py-0.5 sticky top-0">
                    {group}
                  </p>
                )}
                {opts.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    onMouseEnter={() => onHoverOption?.(o.value)}
                    onClick={() => choose(o.value)}
                    className={`w-full text-left text-xs px-2 py-1 flex items-center gap-1 hover:bg-brand-50 ${
                      o.value === value ? 'bg-brand-50' : ''
                    }`}
                  >
                    <span className="flex-1 min-w-0 whitespace-normal break-words">
                      {o.label}
                      {o.key && (
                        <span className="ml-1 text-[10px] text-gray-400 font-mono break-all">{o.key}</span>
                      )}
                    </span>
                    {o.badge && (
                      <span className="shrink-0 text-[9px] text-gray-500 border border-gray-200 rounded px-1">
                        {o.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
